/**
 * CR-8187: Query params for the Submitted Appeals filter panel.
 *
 * One builder shared by the list, the per-tab counts and the CSV export so all
 * three always narrow by the same filters. Status is not mapped here: it
 * depends on the active quick-tab (see AppealsSubTab's `tabStatusParams`).
 */

/** Filter-panel state of the Submitted Appeals sub-tab. */
export interface AppealFilters {
  status: string;
  grn_number: string;
  po_number: string;
  part_number: string;
  /** Supplier company id, as a string. Customer view only. */
  supplier: string;
}

/** No filters set — the initial panel state and what Clear All restores. */
export const EMPTY_APPEAL_FILTERS: AppealFilters = {
  status: '',
  grn_number: '',
  po_number: '',
  part_number: '',
  supplier: '',
};

/**
 * Maps the panel's text and supplier filters to delivery-appeal API params.
 *
 * @param filters - Current filter-panel state
 * @returns Params to merge into a `getDeliveryAppeals` / `exportDeliveryAppeals` call
 */
export function buildAppealFilterParams(filters: AppealFilters): Record<string, string> {
  const params: Record<string, string> = {};
  // CR-8206: trimmed, so a pasted value with a stray space still matches.
  const grn = filters.grn_number.trim();
  const po = filters.po_number.trim();
  const part = filters.part_number.trim();
  if (grn) params['grn__grn_number__icontains'] = grn;
  if (po) params['grn__po_line__po__po_number__icontains'] = po;
  if (part) params['grn__po_line__part_number__icontains'] = part;
  if (filters.supplier) params['grn__po_line__po__supplier'] = filters.supplier;
  return params;
}

/**
 * CR-8239: params for the appeals waiting on the viewer's side — the Action
 * Required tab, its count and the Deliveries "Submitted Appeals" badge. The
 * backend owns the rule (`purchasing.services.appeal_action`): a customer acts
 * on a supplier's appeal awaiting response; a supplier acts on an appeal the
 * customer has queried. Both sides used to send the customer's filter, so a
 * supplier's tab listed appeals waiting on the customer and its badge never lit.
 *
 * @param isSupplierView - The viewer is in the supplier portal
 * @returns Params to merge into a `getDeliveryAppeals` / `exportDeliveryAppeals` call
 */
export function actionRequiredParams(isSupplierView: boolean): Record<string, string> {
  return { action_required: isSupplierView ? 'supplier' : 'customer' };
}
